import { ARENA } from './balance'
import { activeCueBeat, activeTextLine, advDisplayText, advNamePlate, advPictureOnly, advStaging, currentAdvFigure, currentAdvPicture,
  currentAdvPictureFadeMs, choiceRevealAlpha, endsInBlackout, takeAdvDisplayCharacters,
  type AdvFigure, type AdvInsert, type AdvScene, type AdvRuntime } from './adv'
import type { GameState } from './types'
import type { MenuRect } from './menu_layout'
import { inputDeviceView } from './input'
import { drawAdvDissolve } from './adv_dissolve_view'
import { drawAdvLampGlow, drawAdvStatic, drawAdvWind } from './adv_air_view'
import { drawAdvCollage, preloadAdvCollage } from './adv_collage_view'
import { resolveInputText } from './input_labels'
import { uiSans } from './ui_font'
import { advChoiceIdleAlpha, advChoiceIdleColor, advChoiceLook, drawChoiceInk } from './readability_1104'

const images = new Map<string, HTMLImageElement>()
function image(src: string): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null
  let img = images.get(src)
  if (!img) { img = new Image(); img.src = src; images.set(src, img) }
  return img
}
// Main calls this on phase entry; the drawer also preloads for standalone callers.
export function preloadAdvPictures(scene: AdvScene): void {
  for (const line of [...scene.lines, ...(scene.choice?.options.flatMap(o => o.resultLines ?? []) ?? [])]) {
    if (line.kind === 'picture') image(line.src)
    if (line.kind === 'figure' && line.src) image(line.src)
    if (line.kind === 'stage' && line.effect === 'insert' && line.src) { image(line.src); if (line.fig) image(line.fig) }
    if (line.kind === 'stage' && line.effect === 'wind') image(line.src)
    if (line.kind === 'stage' && line.effect === 'collage') preloadAdvCollage()
  }
}
// 場面の頭 (最初の文の行まで) で出す絵。 会話場面の頭を、これが読めるまで黒のまま延ばす (src/battle_asset_gate.ts・11-02)。
const HEAD_LINE_KINDS = new Set(['beat', 'picture', 'figure', 'stage', 'cue'])
export function advSceneHeadSrcs(scene: AdvScene): string[] {
  const srcs: string[] = []
  for (const line of scene.lines) {
    if (line.kind === 'picture') srcs.push(line.src)
    else if (line.kind === 'figure' && line.src) srcs.push(line.src)
    else if (line.kind === 'stage' && line.effect === 'insert' && line.src) { srcs.push(line.src); if (line.fig) srcs.push(line.fig) }
    else if (line.kind === 'stage' && line.effect === 'wind') srcs.push(line.src)
    if (!HEAD_LINE_KINDS.has(line.kind)) break
  }
  return srcs
}
/** 先代の回想 (cue flashback) を含む場面か。 含むなら回想の4枚も頭で待つ。 */
export function advSceneHasFlashback(scene: AdvScene): boolean {
  return scene.lines.some(line => line.kind === 'cue' && line.effect === 'flashback')
}
/** 場面の頭の絵を取りに行き終えたか (読めた・読めなかったの両方を終わりと数える)。 まだなら読み始める。 */
export function advSceneHeadSettled(scene: AdvScene): boolean {
  return advSceneHeadSrcs(scene).every(src => image(src)?.complete !== false)
}
/** その絵を取りに行き終えたか (撮影の道具が「出すべき絵が欠けたコマ」を数えるのに使う)。 読み始めていなければ false。 */
export function advImageSettled(src: string): boolean {
  return images.get(src)?.complete === true
}
function wrap(text: string, width: number, measure: (text: string) => number): string[] {
  const lines: string[] = []
  let row = ''
  for (const char of text) {
    if (row && measure(row + char) > width) {
      if ('、。，．・：；？！」』）］｝〕〉》”’ー〜…'.includes(char)) { lines.push(row + char); row = '' }
      else { lines.push(row); row = char }
    } else row += char
  }
  if (row) lines.push(row)
  return lines
}
// Both drawing and pointer input consume this one layout, including selected decorations.
function choiceLayout(ctx: CanvasRenderingContext2D, rt: AdvRuntime, scene: AdvScene) {
  const choice = scene.choice!
  ctx.save()
  ctx.font = `600 20px ${uiSans()}`
  let top = choice.prompt ? 34 : 0
  const rows = choice.options.map((o, i) => {
    const lines = wrap(i === rt.choiceIndex ? `― ${o.text} ―` : o.text, ARENA.width * .72, t => ctx.measureText(t).width)
    const row = { lines, top, h: lines.length * 32 }
    top += row.h + 16
    return row
  })
  ctx.restore()
  const height = top - 16 + 32
  const y = (ARENA.height * .7 - height) / 2
  const rects = rows.map(row => ({ x: ARENA.width * .14, y: y + 16 + row.top, w: ARENA.width * .72, h: row.h }))
  return { rows, rects, y, height }
}
export function measurePictureChoiceRects(ctx: CanvasRenderingContext2D, state: GameState, scene: AdvScene): MenuRect[] | undefined {
  if (!state.adv?.choosing || !scene.choice) return undefined
  return choiceLayout(ctx, state.adv, scene).rects
}

type Fade = { rt: AdvRuntime; scene: string; cursor: number; src: string | null; previous: string | null; start: number; ms: number }
const fades = new WeakMap<CanvasRenderingContext2D, Fade>()
function cover(ctx: CanvasRenderingContext2D, src: string | null, alpha: number): void {
  const img = src ? image(src) : null
  if (!img?.complete || !img.naturalWidth) return
  const scale = Math.max(ARENA.width / img.naturalWidth, ARENA.height / img.naturalHeight)
  const w = img.naturalWidth * scale, h = img.naturalHeight * scale
  ctx.globalAlpha = alpha
  ctx.drawImage(img, (ARENA.width - w) / 2, (ARENA.height - h) / 2, w, h)
}

// 割り込みの1枚 (push の寄り・切り抜きの lean を当てる)。 読み込みが済むまでは描かない。
function drawInsert(ctx: CanvasRenderingContext2D, ins: AdvInsert, alpha: number): void {
  const img = image(ins.src)
  if (alpha <= 0 || !img?.complete || !img.naturalWidth) return
  const about = (s: number, x: number, y: number) => {
    if (s !== 1) { ctx.translate(ARENA.width * x, ARENA.height * y); ctx.scale(s, s); ctx.translate(-ARENA.width * x, -ARENA.height * y) }
  }
  ctx.save()
  about(ins.zoom.scale, ins.zoom.x, ins.zoom.y)
  if (!ins.fig) about(ins.lean.scale, ins.lean.x, ins.lean.y)
  cover(ctx, ins.src, alpha)
  if (ins.fig) { about(ins.lean.scale, ins.lean.x, ins.lean.y); cover(ctx, ins.fig, alpha) }
  ctx.restore()
}

// 人物絵: 背景の上・文字窓の下に立てる。 入れ替えは fadeMs で重ね、 位置と大きさの変化は 250ms で寄せる。
type FigureGeom = { x: number; bottom: number; height: number }
type FigureFade = {
  scene: string; src: string | null; previous: string | null; previousGeom: FigureGeom | null
  start: number; ms: number; from: FigureGeom; to: FigureGeom; moveStart: number
}
const FIGURE_MOVE_MS = 250
const figureFades = new WeakMap<CanvasRenderingContext2D, FigureFade>()
const geomOf = (f: AdvFigure | null): FigureGeom => ({ x: f?.x ?? .5, bottom: f?.bottom ?? 1, height: f?.height ?? 1 })
function figureGeomAt(fade: FigureFade, now: number): FigureGeom {
  const t = Math.min(1, Math.max(0, (now - fade.moveStart) / FIGURE_MOVE_MS)), k = 1 - (1 - t) ** 3
  const mix = (a: number, b: number) => a + (b - a) * k
  return { x: mix(fade.from.x, fade.to.x), bottom: mix(fade.from.bottom, fade.to.bottom), height: mix(fade.from.height, fade.to.height) }
}
function drawFigureImage(ctx: CanvasRenderingContext2D, src: string | null, g: FigureGeom | null, alpha: number): void {
  const img = src ? image(src) : null
  if (!g || alpha <= 0 || !img?.complete || !img.naturalWidth) return
  const h = ARENA.height * g.height, w = img.naturalWidth * h / img.naturalHeight
  ctx.globalAlpha = alpha
  ctx.drawImage(img, ARENA.width * g.x - w / 2, ARENA.height * g.bottom - h, w, h)
}
function drawFigure(ctx: CanvasRenderingContext2D, rt: AdvRuntime, scene: AdvScene, now: number): void {
  const fig = currentAdvFigure(rt, scene), src = fig?.src ?? null, to = geomOf(fig)
  let fade = figureFades.get(ctx)
  if (!fade || fade.scene !== scene.id) {
    fade = { scene: scene.id, src, previous: null, previousGeom: null, start: now - 1e6, ms: 0, from: to, to, moveStart: now - 1e6 }
  } else if (fade.src !== src) {
    fade = { scene: scene.id, src, previous: fade.src, previousGeom: figureGeomAt(fade, now), start: now, ms: fig?.fadeMs ?? 200, from: to, to, moveStart: now - 1e6 }
  } else if (fade.to.x !== to.x || fade.to.bottom !== to.bottom || fade.to.height !== to.height) {
    fade.from = figureGeomAt(fade, now); fade.to = to; fade.moveStart = now
  }
  figureFades.set(ctx, fade)
  const img = src ? image(src) : null
  // 新しい絵の読み込みが済むまでは前の絵を出したまま待つ (背景と同じ)。
  if (src && (!img?.complete || !img.naturalWidth)) { drawFigureImage(ctx, fade.previous, fade.previousGeom, 1); fade.start = now; return }
  const alpha = fade.ms <= 0 ? 1 : Math.min(1, Math.max(0, (now - fade.start) / fade.ms))
  drawFigureImage(ctx, fade.previous, fade.previousGeom, 1 - alpha)
  drawFigureImage(ctx, src, figureGeomAt(fade, now), alpha)
  if (alpha === 1) { fade.previous = null; fade.previousGeom = null }
  ctx.globalAlpha = 1
}
function box(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, radius: number): void {
  ctx.beginPath(); ctx.roundRect(x, y, w, h, radius); ctx.fill(); ctx.stroke()
}
const colorOf = (kind: string): string => kind === 'voice' ? 'rgba(244,216,120,0.92)'
  : kind === 'other' ? 'rgba(170,160,150,0.85)' : kind === 'narration' ? 'rgba(222,216,234,0.94)'
  : kind === 'boss' ? 'rgba(214,196,168,0.9)' : '#ddd0f2'

export function drawPictureInterlude(ctx: CanvasRenderingContext2D, state: GameState, scene: AdvScene, doneLabel: string): void {
  preloadAdvPictures(scene)
  ctx.save()
  ctx.globalAlpha = 1; ctx.fillStyle = '#000'; ctx.fillRect(0, 0, ARENA.width, ARENA.height)
  const rt = state.adv
  if (!rt) { ctx.restore(); return }
  const src = currentAdvPicture(rt, scene), now = performance.now()
  let fade = fades.get(ctx)
  // 場面が替わったら前の場面の絵を引き継がない (前のボスの絵が頭に一瞬重なっていた・10-28 作者「前のボスの絵がちらついたりする」)。
  if (!fade || fade.scene !== scene.id) fade = { rt, scene: scene.id, cursor: rt.cursor, src, previous: null, start: now - 1e6, ms: 500 }
  else if (fade.src !== src) fade = { rt, scene: scene.id, cursor: rt.cursor, src, previous: fade.src, start: now, ms: currentAdvPictureFadeMs(rt, scene) }
  fade.rt = rt; fade.scene = scene.id; fade.cursor = rt.cursor
  fades.set(ctx, fade)
  // 気持ちの見せ方: 揺れは背景・人物・割り込みまで (文字窓は読めるよう動かさない)、 寄りは背景と人物だけ。
  const staging = advStaging(rt, scene), shakeOn = state.settings?.shakeOn !== false
  ctx.save()
  if (shakeOn && (staging.shake.x || staging.shake.y)) ctx.translate(staging.shake.x, staging.shake.y)
  ctx.save()
  // 揺れで画面の端の黒が見えないよう、 揺れの分だけ大きく描く。
  const shakeMag = shakeOn ? Math.max(Math.abs(staging.shake.x), Math.abs(staging.shake.y)) : 0
  const { x: zx, y: zy } = staging.zoom, scale = staging.zoom.scale * (1 + 2.2 * shakeMag / ARENA.height)
  if (scale !== 1) {
    ctx.translate(ARENA.width * zx, ARENA.height * zy); ctx.scale(scale, scale); ctx.translate(-ARENA.width * zx, -ARENA.height * zy)
  }
  const img = src ? image(src) : null
  // Keep the already displayed picture until decoding completes, then run the full fade.
  if (!img?.complete || !img.naturalWidth) {
    cover(ctx, fade.previous, 1); fade.start = now
  } else {
    const alpha = fade.ms <= 0 ? 1 : Math.min(1, Math.max(0, (now - fade.start) / fade.ms))
    cover(ctx, fade.previous, 1); cover(ctx, src, alpha)
    if (alpha === 1) fade.previous = null
  }
  ctx.globalAlpha = 1
  // 風で流れる1枚 (外套) は背景のすぐ上、 漂う光はその上。
  const windImg = staging.wind ? image(staging.wind.src) : null
  if (staging.wind && windImg?.complete && windImg.naturalWidth) { drawAdvWind(ctx, windImg, staging.wind); ctx.globalAlpha = 1 }
  if (staging.lamp) drawAdvLampGlow(ctx, staging.lamp, now)
  ctx.save(); if (staging.figure.x || staging.figure.y) ctx.translate(staging.figure.x, staging.figure.y)
  drawFigure(ctx, rt, scene, now)
  ctx.restore()
  // 光になって消える層は、 寄りと揺れに合わせて絵の上へ描く。
  if (staging.dissolve) drawAdvDissolve(ctx, staging.dissolve)
  ctx.restore()
  // 紙芝居の1枚は、 寄りの外で画面ぜんたいを覆う (揺れだけは受ける)。
  if (staging.collage) drawAdvCollage(ctx, staging.collage)
  if (staging.insert) {
    if (staging.insert.under) drawInsert(ctx, staging.insert.under, staging.insert.under.alpha)
    drawInsert(ctx, staging.insert, staging.insert.alpha)
    ctx.globalAlpha = 1
  }
  // 砂嵐は絵ぜんたいの上・文字窓の下。
  if (staging.static) drawAdvStatic(ctx, staging.static)
  ctx.restore()
  // 絵だけ見せる間は、 下の影と文字窓を描かない (暗転・白の一瞬はそのまま)。
  const pictureOnly = advPictureOnly(rt, scene)
  if (!pictureOnly) {
    const shade = ctx.createLinearGradient(0, ARENA.height * .65, 0, ARENA.height)
    shade.addColorStop(0, 'rgba(0,0,0,0)'); shade.addColorStop(1, 'rgba(0,0,0,0.8)')
    ctx.fillStyle = shade; ctx.fillRect(0, ARENA.height * .65, ARENA.width, ARENA.height * .35)
    if (rt.choosing && scene.choice) {
      ctx.fillStyle = 'rgba(0,0,0,0.45)'; ctx.fillRect(0, 0, ARENA.width, ARENA.height)
      const layout = choiceLayout(ctx, rt, scene), alpha = choiceRevealAlpha(rt)
      ctx.globalAlpha = alpha
      const shadow = ctx.createLinearGradient(0, layout.y - 14, 0, layout.y + layout.height + 14)
      shadow.addColorStop(0, 'rgba(0,0,0,0)'); shadow.addColorStop(.3, 'rgba(0,0,0,0.5)')
      shadow.addColorStop(.7, 'rgba(0,0,0,0.5)'); shadow.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = shadow; ctx.fillRect(0, layout.y - 14, ARENA.width, layout.height + 28)
      ctx.textAlign = 'center'
      if (scene.choice.prompt) { ctx.font = `17px ${uiSans()}`; ctx.fillStyle = '#ddd0f2'; ctx.fillText(scene.choice.prompt, ARENA.width / 2, layout.y + 34) }
      ctx.font = `600 20px ${uiSans()}`
      layout.rows.forEach((row, i) => {
        const sel = i === rt.choiceIndex, rect = layout.rects[i]!
        // 読みやすさの比較版 (DEV ?advChoice=a|b・src/readability_1104.ts)。配布版は帯なし・選んでいない方 0.5。
        if (import.meta.env.DEV) {
          ctx.font = `600 20px ${uiSans()}`
          const tw = Math.max(...row.lines.map(l => ctx.measureText(l).width))
          drawChoiceInk(ctx, rect.x + rect.w / 2, rect.y + rect.h / 2 + 2, tw + 150, rect.h + 26, sel, alpha)
        }
        ctx.globalAlpha = alpha * (sel ? 1 : import.meta.env.DEV ? advChoiceIdleAlpha() : .5)
        ctx.fillStyle = sel ? '#f4d878' : import.meta.env.DEV ? advChoiceIdleColor() : '#b9a3d9'
        row.lines.forEach((line, j) => ctx.fillText(line, rect.x + rect.w / 2, rect.y + (j + 1) * 32 - 8))
      })
      if (import.meta.env.DEV && advChoiceLook() === 'b' && !rt.done) {
        const last = layout.rects[layout.rects.length - 1]!
        ctx.globalAlpha = alpha * .9; ctx.font = `16px ${uiSans()}`; ctx.fillStyle = '#d9cbef'
        ctx.fillText(resolveInputText(inputDeviceView().device === 'pad' ? '{move}で選ぶ ・ {retry}で決める' : '選びたい方をクリック', inputDeviceView()), ARENA.width / 2, last.y + last.h + 30)
      }
    }
    ctx.globalAlpha = 1
    const x = ARENA.width * (60 / 1600), y = ARENA.height * .74
    const w = ARENA.width - x * 2, h = ARENA.height * .22
    ctx.fillStyle = 'rgba(10,7,20,0.80)'; ctx.strokeStyle = 'rgba(150,138,185,0.65)'; ctx.lineWidth = 2 * ARENA.width / 1600
    box(ctx, x, y, w, h, 8)
    const line = activeTextLine(rt, scene)
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic'
    if (line && !rt.choosing && !rt.done) {
      const plate = advNamePlate(line)
      if (plate) {
        ctx.font = `600 15px ${uiSans()}`
        const pw = ctx.measureText(plate).width + 28
        box(ctx, x + 18, y - 17, pw, 28, 5)
        ctx.fillStyle = colorOf(line.kind); ctx.fillText(plate, x + 32, y + 3)
      }
      ctx.font = `21px ${uiSans()}`; ctx.fillStyle = colorOf(line.kind)
      const full = advDisplayText(line, true), revealed = takeAdvDisplayCharacters(full, rt.revealedChars)
      // Wrap the full string first so already revealed characters never reflow.
      let remaining = Array.from(revealed).length
      wrap(full, w - 52, t => ctx.measureText(t).width).slice(0, 3).forEach((row, i) => {
        ctx.fillText(takeAdvDisplayCharacters(row, remaining), x + 26, y + 42 + i * 32)
        remaining -= Array.from(row).length
      })
      if (rt.revealedChars >= Array.from(full).length) { ctx.font = `14px ${uiSans()}`; ctx.fillText('▼', x + w - 28, y + h - 14) }
    }
    if (rt.done || (rt.choosing && !(import.meta.env.DEV && advChoiceLook() === 'b'))) {
      ctx.font = `16px ${uiSans()}`; ctx.fillStyle = '#b9a3d9'; ctx.textAlign = 'center'
      const hint = rt.done ? doneLabel : inputDeviceView().device === 'pad' ? '{move}で選ぶ ・ {retry}で決める' : '選びたい方をクリック'
      ctx.fillText(resolveInputText(hint, inputDeviceView()), ARENA.width / 2, y + h / 2)
    }
  }
  // 暗転: cue 'blackout' の後の beat で黒をかぶせ、 場面が暗転で終わるなら完走後も黒のまま。
  const cue = activeCueBeat(rt, scene)
  const black = cue?.effect === 'blackout' ? cue.progress : rt.done && endsInBlackout(scene) ? 1 : 0
  if (staging.flash > 0) { ctx.globalAlpha = staging.flash; ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, ARENA.width, ARENA.height) }
  if (black > 0) { ctx.globalAlpha = black; ctx.fillStyle = '#000'; ctx.fillRect(0, 0, ARENA.width, ARENA.height) }
  ctx.restore()
}
